<template>
  <div class="page-block">
    <bank-select-dialog
      name="bankSelect"
      :is-bank-mode="true"
      :is-shown="bankSelectDialogShown"
      @select="onBankSelected"
      @closed="bankSelectDialogShown = false"
      @cancel-clicked="bankSelectDialogShown = false" />
    <bank-select-dialog
      name="bankBranchSelect"
      :is-bank-mode="false"
      :is-shown="bankBranchSelectDialogShown"
      :bank-code="item.bank_code"
      @select="onBankBranchSelected"
      @closed="bankBranchSelectDialogShown = false"
      @cancel-clicked="bankBranchSelectDialogShown = false" />
    <div
      class="page-block__heading page-block__heading--withbar">
      <h3 class="page-block__title">
        アカウント情報入力
      </h3>
    </div>
    <div class="page-block__inner">
      <release-step-area
        v-if="!hideSteps && feature === 'release'"
        :index="4" />
      <alert-view
        :errors="errors"
        :bankname-faq-url="banknameFaqUrl" />
      <form
        :class="{ 'form--error': errors }">
        <div class="profile-info">
          <div class="form-wrap form-wrap--profile u-no-margin-top">
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="artist_name_domestic">アカウント管理者氏名
                  <span class="req">＊</span>
                </label>
              </div>
              <div
                class="form-item">
                <input
                  id="account_administrator_name"
                  v-model="item.contact_name"
                  class="form-control"
                  type="text"
                  @input="clearError('contact_name')">
                <span>
                  ※ 必ず正しい氏名を入力してください。<br />
                  ※ 収益を受け取る銀行口座の口座名義と同一の名前を登録してください。
                </span>
                <p
                  v-if="errors['contact_name']"
                  class="error-text">
                  {{ errors['contact_name'][0] }}
                </p>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label class="control-label">
                  生年月日
                  <span class="req">＊</span>
                </label>
              </div>
              <div
                class="form-item">
                <date-picker
                  v-model="item.dob"
                  :default-value="getDefaultDate()"
                  :disabled-date="disabledDates"
                  :editable="false"
                  value-type="format">
                </date-picker>
                <p
                  v-if="errors['dob']"
                  class="error-text">
                  {{ errors['dob'][0] }}
                </p>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="zip_1">郵便番号
                </label>
                <span class="req">＊</span>
              </div>
              <div class="form-item">
                <input
                  id="zip_1"
                  v-model="zip1"
                  class="form-control form-control--postal1"
                  type="text"
                  placeholder="123"
                  maxlength="3"
                  @input="clearError('postal_code')">
                <input
                  id="zip_2"
                  v-model="zip2"
                  class="form-control form-control--postal2"
                  type="text"
                  placeholder="1234"
                  maxlength="4"
                  @input="clearError('postal_code')">
                <a
                  class="c-btn c-btn--postal"
                  :class="{ 'c-btn--off': searchButtonDisabled }"
                  href="#"
                  @click.prevent="onSearchPostalCodeClicked">検索
                  <span class="c-btn__icon-arrow"></span>
                </a>
                <p
                  v-if="errors['postal_code']"
                  class="error-text">
                  {{ errors['postal_code'][0] }}
                </p>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="pref">都道府県
                </label>
                <span class="req">＊</span>
              </div>
              <div class="form-item">
                <div class="c-select_wrap">
                  <select
                    id="pref"
                    v-model="item.prefecture"
                    class="form-control c-select"
                    name="language">
                    <option
                      v-for="prefecture in prefectures"
                      :key="prefecture"
                      :value="prefecture">
                      {{ prefecture }}
                    </option>
                  </select>
                </div>
                <p
                  v-if="errors['prefecture']"
                  class="error-text">
                  {{ errors['prefecture'][0] }}
                </p>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="address1">市区町村 / 番地
                </label>
                <span class="req">＊</span>
              </div>
              <div class="form-item">
                <input
                  id="address1"
                  v-model="item.address1"
                  class="form-control"
                  type="text"
                  placeholder="例）東京都渋谷区道玄坂1-1-1"
                  @input="clearError('address1')">
                <p
                  v-if="errors['address1']"
                  class="error-text">
                  {{ errors['address1'][0] }}
                </p>
              </div>
            </div>
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label"
                  for="address2">建物名
                </label>
              </div>
              <div class="form-item">
                <input
                  id="address2"
                  v-model="item.address2"
                  class="form-control"
                  type="text"
                  placeholder="例）江戸ビル1111">
                <p
                  v-if="errors['address2']"
                  class="error-text">
                  {{ errors['address2'][0] }}
                </p>
              </div>
            </div>
            <!-- Bank Name -->
            <div class="form-group">
              <div class="form-label">
                <label
                  class="control-label">銀行名
                </label>
                <span class="req">＊</span>
              </div>
              <div class="form-item form-item--change-btn">
                <span v-if="item.bank_name">
                  ({{ item.bank_code }}) {{ item.bank_name }}
                </span>
                <span
                  v-else
                  class="error-text">
                  未登録
                </span>
                <button
                  type="button"
                  class="c-btn c-btn--narrow"
                  @click.prevent="onSelectBankClicked">
                  銀行名を選択
                  <span class="c-btn__icon-add">
                    <span></span>
                  </span>
                </button>
              </div>
              <p
                v-if="errors['bank_name']"
                class="error-text">
                {{ errors['bank_name'][0] }}
              </p>
            </div>
            <!-- Bank Name End-->
            <!-- Bank Branch Name Start-->
            <template>
              <div
                class="form-group">
                <div class="form-label">
                  <label
                    class="control-label">支店名
                  </label>
                  <span class="req">＊</span>
                </div>
                <div class="form-item form-item--change-btn">
                  <span v-if="item.bank_branch_name">
                    ({{ item.bank_branch_code }}) {{ item.bank_branch_name }}
                  </span>
                  <span
                    v-else
                    class="error-text">
                    未登録
                  </span>
                  <button
                    type="button"
                    class="c-btn c-btn--narrow"
                    @click.prevent="onSelectBankBranchClicked">
                    支店名を選択
                    <span class="c-btn__icon-add">
                      <span></span>
                    </span>
                  </button>
                  <p
                    v-if="errors['bank_branch_name']"
                    class="error-text">
                    {{ errors['bank_branch_name'][0] }}
                  </p>
                </div>
              </div>
              <template>
                <div class="form-group">
                  <div class="form-label">
                    <label
                      class="control-label"
                      for="bank_account_type">口座種別
                    </label>
                    <span class="req">＊</span>
                  </div>
                  <div class="form-item">
                    <div class="c-select_wrap">
                      <select
                        id="bank_account_type"
                        v-model="item.bank_account_type"
                        class="form-control c-select"
                        name="language">
                        <option
                          v-for="type1 in bankAccountTypes"
                          :key="type1.id"
                          :value="type1.id">
                          {{ type1.text }}
                        </option>
                      </select>
                    </div>
                    <p
                      v-if="errors['bank_account_type']"
                      class="error-text">
                      {{ errors['bank_account_type'][0] }}
                    </p>
                  </div>
                </div>
                <div class="form-group">
                  <div class="form-label">
                    <label
                      class="control-label"
                      for="bank_account_number">口座番号
                    </label>
                    <span class="req">＊</span>
                  </div>
                  <div class="form-item">
                    <input
                      id="bank_account_number"
                      v-model="item.bank_account_code"
                      maxlength="7"
                      class="form-control form-control--account-number"
                      type="text"
                      @input="clearError('bank_account_code')">
                    <span class="form-item-text--inline">7桁未満の場合は頭に0を記入してください。</span>
                    <!-- eslint-disable vue/no-v-html -->
                    <p
                      v-if="errors['bank_account_code']"
                      class="error-text"
                      v-html="errors['bank_account_code'][0]">
                    </p>
                    <!-- eslint-enable vue/no-v-html -->
                  </div>
                </div>
                <div class="form-group">
                  <div class="form-label form-label--top">
                    <label
                      class="control-label"
                      for="bank_account_name">口座名義
                    </label>
                    <span class="req">＊</span>
                  </div>
                  <div class="form-item">
                    <div class="form-label">
                      <label
                        class="control-label">上で入力したアカウント管理者氏名と銀行口座名義は同一名義ですか？
                      </label>
                    </div>
                    <div
                      class="form-item-inline "
                      style="margin-bottom: 20px">
                      <span class="radio">
                        <label for="true">
                          <input
                            id="true"
                            v-model="isNameSame"
                            name="isDebut"
                            type="radio"
                            value="はい">
                          <div class="option-input"></div> はい
                        </label>
                      </span>
                      <span class="radio">
                        <label for="false">
                          <input
                            id="false"
                            v-model="isNameSame"
                            name="isDebut"
                            type="radio"
                            value="いいえ">
                          <div class="option-input"></div> いいえ
                        </label>
                      </span>
                    </div>
                    <div v-if="isNameSame === 'はい'">
                      <input
                        id="bank_account_name"
                        v-model="item.bank_account_name"
                        class="form-control"
                        type="text"
                        @input="clearError('bank_account_name')">
                      <div class="form-item-text form-item-text--bottom">
                        <strong>
                          <router-link
                            :to="banknameFaqUrl"
                            class="c-text-link"
                            target="_blank">銀行口座名義で使用できる文字は?
                          </router-link>
                        </strong>
                        <br>
                        販売利益をアカウント管理者様にお支払するために必要な口座になりますので、お手元に通帳をご用意の上、お間違いないよう入力してください。
                        口座情報の不備により当社が振込をできなかった場合、銀行の組み戻し費用はアカウント管理者様の負担となります。
                      </div>
                      <!-- eslint-disable vue/no-v-html -->
                      <p
                        v-if="errors['bank_account_name']"
                        class="error-text"
                        v-html="errors['bank_account_name'][0]">
                      </p>
                      <!-- eslint-enable vue/no-v-html -->
                    </div>
                    <div v-if="isNameSame === 'いいえ'">
                      <p class="error-text ">
                        販売収益のお振込先となる銀行口座は、アカウント管理者氏名と同一名義である必要があります。アカウント管理者氏名は正しい氏名でご登録ください。
                        また、一度ご登録いただいたアカウント管理者氏名は原則変更ができません。
                        万一変更が必要な場合は、<a
                          href="/client/contact"
                          style="color: red; text-decoration: underline;">お問い合わせ</a>ください。
                      </p>
                    </div>
                  </div>
                </div>
              </template>
            </template>
            <!-- Bank Branch Name End-->
          </div>
        </div>
      </form>
      <div
        class="c-btn-wrap">
        <a
          class="c-btn"
          :class="{ 'c-btn--off': isNameSame !== 'はい' }"
          href="#"
          @click.prevent="onNextClicked">
          <span>アカウント情報の確認へ</span>
          <span class="c-btn__icon-arrow"></span>
        </a>
        <a
          v-if="feature === 'release'"
          href="#"
          class="c-btn c-btn--back c-btn--back-left"
          @click.prevent="onBackClicked()">
          <span class="c-btn__icon-arrow c-btn__icon-arrow--left"></span>
          ストア選択へ戻る
        </a>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>

<script>
import DatePicker from 'vue2-datepicker'
import moment from 'moment/moment'
import AlertView from './AlertView'
import BankSelectDialog from './BankSelectDialog'
import 'vue2-datepicker/index.css'
import 'vue2-datepicker/locale/ja'
import ReleaseStepArea from '../Release/StepArea'

export default {
  name: 'AccountInformation',
  components: {
    AlertView,
    BankSelectDialog,
    DatePicker,
    ReleaseStepArea,
  },
  props: {
    companyId: {
      type: [String, Number],
      required: true,
    },
    hideSteps: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      type: 'single',
      item: {
        bank_code: '',
        bank_name: '',
        bank_branch_name: '',
        bank_branch_code: '',
        bank_account_type: '',
        bank_account_code: '',
        bank_account_name: '',
        prefecture: '',
        address1: '',
        address2: '',
        contact_name: '',
        dob: '',
        postal_code: '',
      },
      zip1: '',
      zip2: '',
      errors: {},
      bankAccountTypes: [
        {
          id: 0,
          text: '普通預金',
        },
        {
          id: 1,
          text: '当座預金',
        },
      ],
      isNameSame: '',
      prefectures: [
        '北海道',
        '青森県',
        '岩手県',
        '宮城県',
        '秋田県',
        '山形県',
        '福島県',
        '茨城県',
        '栃木県',
        '群馬県',
        '埼玉県',
        '千葉県',
        '東京都',
        '神奈川県',
        '新潟県',
        '富山県',
        '石川県',
        '福井県',
        '山梨県',
        '長野県',
        '岐阜県',
        '静岡県',
        '愛知県',
        '三重県',
        '滋賀県',
        '京都府',
        '大阪府',
        '兵庫県',
        '奈良県',
        '和歌山県',
        '鳥取県',
        '島根県',
        '岡山県',
        '広島県',
        '山口県',
        '徳島県',
        '香川県',
        '愛媛県',
        '高知県',
        '福岡県',
        '佐賀県',
        '長崎県',
        '熊本県',
        '大分県',
        '宮崎県',
        '鹿児島県',
        '沖縄県',
      ],
      bankSelectDialogShown: false,
      bankBranchSelectDialogShown: false,
      banknameFaqUrl: '',
      feature: 'release',
    }
  },
  computed: {
    searchButtonDisabled() {
      return (this.zip1?.length !== 3 || this.zip2?.length !== 4)
    },
  },
  mounted() {
    if (this.isBtoB) {
      this.banknameFaqUrl = '/client/faqs/108'
    } else if (this.isBtoC) {
      this.banknameFaqUrl = '/client/faqs/107'
    }
    if (window.sessionStorage.getItem('account_information')) {
      this.item = JSON.parse(window.sessionStorage.getItem('account_information'))
      this.zip1 = this.item.postal_code?.substring(0, 3)
      this.zip2 = this.item.postal_code?.substring(3)
      this.isNameSame = 'はい'
      this.feature = this.item?.feature || 'release'
    }
  },
  methods: {
    // Display date from 18 years ago on dae of birth calendar
    getDefaultDate() {
      const currentDate = new Date()
      return new Date(currentDate.getFullYear() - 18, currentDate.getMonth(), currentDate.getDate())
    },
    async onSearchPostalCodeClicked() {
      const response = await window.axios.get('/api/client/postal-code', {
        params: {
          code1: this.zip1,
          code2: this.zip2,
        },
      })
      if (response.data.length > 0) {
        const {
          address,
          city,
          prefecture,
        } = response.data[0]
        this.item.prefecture = prefecture
        this.item.address1 = `${city}${address}`
      }
    },
    onSelectBankClicked() {
      this.bankSelectDialogShown = true
    },
    onBankSelected(context) {
      const {
        code,
        name,
      } = context
      if (this.item.bank_code !== code) {
        this.item.bank_branch_name = null
        this.item.bank_branch_code = null
      }
      this.item.bank_name = name
      this.item.bank_code = code
    },
    onSelectBankBranchClicked() {
      this.bankBranchSelectDialogShown = true
    },
    onBankBranchSelected(context) {
      const {
        code,
        name,
      } = context
      this.item.bank_branch_name = name
      this.item.bank_branch_code = code
    },
    async onNextClicked() {
      this.errors = {}
      try {
        this.item.postal_code = `${this.zip1}${this.zip2}`
        this.item.dob = this.item.dob ? moment(this.item.dob).format('YYYY-MM-DD') : ''
        await window.axios.put(`/api/client/companies/${this.companyId}?form_validate_only=1`, this.item)
        window.sessionStorage.setItem('account_information', JSON.stringify(this.item))
        this.$emit('showSection')
      } catch ({ response }) {
        const { errors } = response.data
        this.errors = errors
        window.scrollTo(0, 0)
      }
    },
    onBackClicked() {
      this.$router.push({ name: `${this.$route.meta.sideMenuId}-step2` })
    },
    clearError(fieldName) {
      this.errors[fieldName] = ''
    },
    disabledDates(date) {
      const currentDate = new Date()
      const minDate = new Date(1900, 0, 1)
      const maxDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate(),
      )

      // Disable dates before 1900 and future dates
      return date < minDate || date > maxDate
    },
  },
}
</script>
<style scoped>
@media screen and (max-width: 895px) {
  .page-block__heading {
    padding: 1.3333333333vw 2.6666666667vw;
    min-height: 13.3333333333vw;
  }
}
</style>
